import makeStyles from '@mui/styles/makeStyles';
import Popover from "@mui/material/Popover";
import classNames from "classnames";
import {Check, ChevronDown} from "lucide-react";
import {filter, find, includes, isEmpty, map, without} from 'lodash';
import React, {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {color, font, radius, shadow, state, type} from "common/tokens";

const SEARCH_FROM = 10;

const useStyles = makeStyles({
  field: {
    ...type.body,
    appearance: 'none',
    width: '100%',
    minHeight: 52,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    textAlign: 'left',
    padding: '14px 16px',
    background: color.SURFACE,
    border: `1px solid ${color.BORDER}`,
    borderRadius: 12,
    color: color.TEXT,
    cursor: 'pointer',
    '&:hover': {
      borderColor: color.TEXT_MUTED,
    },
    // Matches the other form controls: the field keeps its own colours and
    // fades as a whole, so the value stays readable.
    '&:disabled': {
      opacity: state.DISABLED_OPACITY,
      cursor: 'not-allowed',
      borderColor: color.BORDER,
    },
  },
  open: {
    borderColor: color.ACCENT,
  },
  invalid: {
    borderColor: color.DANGER,
  },
  value: {
    flex: 1,
    minWidth: 0,
  },
  placeholder: {
    color: color.TEXT_SUBTLE,
  },
  chevron: {
    color: color.TEXT_MUTED,
    flexShrink: 0,
  },
  paper: {
    borderRadius: radius.PANEL,
    boxShadow: shadow.POPOVER,
    border: `1px solid ${color.DIVIDER}`,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 360,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    borderBottom: `1px solid ${color.DIVIDER}`,
  },
  search: {
    ...type.body,
    flex: 1,
    minWidth: 0,
    padding: '10px 12px',
    border: `1px solid ${color.BORDER}`,
    borderRadius: 10,
    color: color.TEXT,
    outline: 'none',
    '&:focus': {
      borderColor: color.ACCENT,
    },
  },
  count: {
    ...type.meta,
    color: color.ACCENT_TEXT,
    whiteSpace: 'nowrap',
    margin: 0,
  },
  list: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 6,
  },
  option: {
    ...type.body,
    appearance: 'none',
    background: 'none',
    border: 'none',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    textAlign: 'left',
    padding: '10px 12px',
    borderRadius: 8,
    color: color.TEXT,
    cursor: 'pointer',
    '&:hover': {
      background: color.SURFACE_MUTED,
    },
  },
  optionSelected: {
    background: color.ACCENT_TINT,
  },
  optionDisabled: {
    opacity: 'var(--ui-disabled-opacity)',
    cursor: 'default',
    '&:hover': {
      background: 'none',
    },
  },
  box: {
    width: 20,
    height: 20,
    flexShrink: 0,
    border: `1px solid ${color.BORDER}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'transparent',
  },
  boxMultiple: {
    borderRadius: 6,
  },
  boxSingle: {
    borderRadius: radius.PILL,
  },
  boxSelected: {
    background: color.ACCENT,
    borderColor: color.ACCENT,
    color: color.ON_ACCENT,
  },
  optionBody: {
    flex: 1,
    minWidth: 0,
  },
  optionHelp: {
    ...type.meta,
    display: 'block',
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: '2px 0 0',
  },
  customTrigger: {
    display: 'inline-flex',
  },
  empty: {
    ...type.body,
    color: color.TEXT_MUTED,
    textAlign: 'center',
    margin: '24px 0',
  },
});

const SelectField = ({
  options = [],
  value = [],
  onChange,
  multiple = false,
  placeholder,
  maxSelections,
  invalid = false,
  disabled = false,
  renderTrigger,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const anchor = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedKeys = multiple ? value : without([value], undefined, null, '');
  const searchable = options.length >= SEARCH_FROM;

  const shown = query
    ? filter(options, option => includes(String(option.label).toLowerCase(), query.toLowerCase()))
    : options;

  const label = map(selectedKeys, key => {
    const option = find(options, item => item.key === key);
    return option ? option.label : null;
  }).filter(Boolean).join(', ');

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const pick = option => {
    if (option.disabled) return;

    if (!multiple) {
      onChange(option.key);
      close();
      return;
    }

    const isSelected = includes(selectedKeys, option.key);
    const next = isSelected
      ? without(selectedKeys, option.key)
      : [...selectedKeys, option.key];

    if (!isSelected && maxSelections && next.length > maxSelections) return;
    onChange(next);
  };

  return (
    <>
      {renderTrigger ? (
        <span ref={anchor} className={classes.customTrigger}>
          {renderTrigger(() => setOpen(true))}
        </span>
      ) : (
      <button
        type={'button'}
        ref={anchor}
        disabled={disabled}
        aria-haspopup={'listbox'}
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        className={classNames(classes.field, open && classes.open, invalid && classes.invalid)}
        onClick={() => setOpen(true)}
      >
        <span className={classNames(classes.value, isEmpty(label) && classes.placeholder)}>
          {isEmpty(label) ? (placeholder || t('selectCaps')) : label}
        </span>
        <ChevronDown size={18} className={classes.chevron}/>
      </button>
      )}

      <Popover
        open={open}
        anchorEl={anchor.current}
        onClose={close}
        classes={{paper: classes.paper}}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        marginThreshold={8}
        PaperProps={{style: renderTrigger
          ? {minWidth: 280}
          : {width: anchor.current ? anchor.current.offsetWidth : undefined}}}
      >
        <div className={classes.panel}>
          {(searchable || multiple) && (
            <div className={classes.head}>
              {searchable && (
                <input
                  autoFocus
                  className={classes.search}
                  placeholder={t('search')}
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                />
              )}
              {multiple && (
                <p className={classes.count}>{`${selectedKeys.length} ${t('selected')}`}</p>
              )}
            </div>
          )}

          <div className={classes.list} role={'listbox'}>
            {isEmpty(shown) ? (
              <p className={classes.empty}>{t('noOptions')}</p>
            ) : (
              map(shown, option => {
                const isSelected = includes(selectedKeys, option.key);
                const isBlocked =
                  option.disabled ||
                  (multiple && !isSelected && maxSelections && selectedKeys.length >= maxSelections);

                return (
                  <button
                    type={'button'}
                    key={option.key}
                    role={'option'}
                    aria-selected={isSelected}
                    className={classNames(
                      classes.option,
                      isSelected && classes.optionSelected,
                      isBlocked && classes.optionDisabled
                    )}
                    onClick={() => !isBlocked && pick(option)}
                  >
                    <span
                      className={classNames(
                        classes.box,
                        multiple ? classes.boxMultiple : classes.boxSingle,
                        isSelected && classes.boxSelected
                      )}
                    >
                      <Check size={14} strokeWidth={3}/>
                    </span>
                    <span className={classes.optionBody}>
                      {option.label}
                      {Boolean(option.helpText) && (
                        <span className={classes.optionHelp}>{option.helpText}</span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default SelectField;
